<template>

  <v-card
      class='position'
      v-if="show">

    <v-toolbar :color="color" dark>
      <v-icon>{{icon}}</v-icon>
      <v-toolbar-title class="wgu-win-title">{{componentTitle}}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-app-bar-nav-icon @click="show = false"><v-icon>close</v-icon></v-app-bar-nav-icon>
    </v-toolbar>

    <!-- div needed to make card content scrollable without toolbar in mobile view -->
    <div class="card-content">

      <v-img
        v-if="windowItems.image && windowItems.image.value"
        :height="imageHeight"
        :src="windowItems.image.value"
      ></v-img>
      <p
        class="photo-attribution"
        v-if="windowItems.image && windowItems.image.value"
      >
        Quelle Foto: <a :href="windowItems.image.value" target="_blank">{{windowItems.image.value}}</a>
      </p>

      <v-card-title
        v-if="windowItems.title && windowItems.title.value"
      >
      {{windowItems.title.value}}
      </v-card-title>

      <v-card-subtitle
        v-if="windowItems.description && windowItems.description.value"
      >
      {{windowItems.description.value}}
      </v-card-subtitle>

      <slot
        name="poiinfo-after-subtitle"
        v-bind:featureProperties="featureProperties">
      </slot>

      <v-card-text
      >

      <v-list
      dense
      >
        <!-- Item with Icons -->
        <v-list-item
          v-for="(item, key) in iconItems"
          v-bind:key="key"
        >
          <v-list-item-content>
            <v-icon class="poi-icon">
              {{item.icon}}
            </v-icon>
          </v-list-item-content>

          <v-list-item-content>
            <v-list-item-title>{{item.value}}</v-list-item-title>
          </v-list-item-content>

        </v-list-item>

        <!-- Items without icons but names -->
        <v-list-item
        v-for="(item, key) in namedItems"
        v-bind:key="key"
        >

          <v-list-item-content>
            <v-list-item-title><b>{{item.displayKey || item.key}}</b></v-list-item-title>
          </v-list-item-content>

          <v-list-item-content>
            <v-list-item-title>{{item.value}}</v-list-item-title>
          </v-list-item-content>

        </v-list-item>

      </v-list>

    </v-card-text>

    <!-- buttons with links to websites -->
    <v-card-actions
      v-if="linkItems"
      >
        <v-btn
        v-for="(config, key) in linkItems"
        v-bind:key="key"
        :href=config.url
        target="_blank"
        :color="color"
        dark
        small
        >
          <v-icon
            left
            v-if="config.icon"
          >
            {{config.icon}}
          </v-icon>
          {{config.title}}
        </v-btn>
    </v-card-actions>

  </div>

  </v-card>

</template>

<script>
import LayerUtil from '../../src/util/Layer';
import { WguEventBus } from '../../src/WguEventBus';
import { Mapable } from '../../src/mixins/Mapable.js';

/**
 * Card that shows information about a POI.
 */
export default {
  mixins: [Mapable],
  props: {
    color: { type: String, required: false, default: 'red darken-3' },
    icon: { type: String, required: false, default: 'info' },
    componentTitle: { type: String, required: false, default: 'Info' }
  },
  data () {
    return {
      show: false,

      imageHeight: 150,

      /** not displayed in the list, but on the component's window */
      windowProperties: {
        /** the heading of the window content */
        title: {
          possibleKeys: ['name', 'title', 'station_name']
        },
        /** the description below the heading */
        description: {
          possibleKeys: ['description']
        },
        /** the image above the list */
        image: {
          possibleKeys: ['image_url', 'image', 'photo', 'photo_url']
        }
      },

      /** displayed in the list with icons */
      iconProperties: {
        address: {
          possibleKeys: ['station_id'],
          icon: 'location_on'
        },
        phone: {
          possibleKeys: ['phone', 'telephone'],
          icon: 'call'
        },
        contact: {
          possibleKeys: ['contact'],
          icon: 'perm_identity'
        },
        email: {
          possibleKeys: ['email'],
          icon: 'email'
        }
      },

      /** displayed as buttons at the bottom */
      linkProperties: {
        website: {
          possibleKeys: ['website', 'homepage', 'www'],
          icon: 'language',
          title: 'Website'
        }
      },

      /** displayed in the list with custom names */
      customNamedProperties: {},

      /** not displayed at all */
      excludedProperties: [],

      windowItems: {},
      namedItems: {},
      iconItems: {},
      linkItems: {},
      properties: {},
      featureProperties: {},
      layerId: undefined,
      defaultExcludedProperties: [
        'boundedBy' // automatically created by OpenLayers
      ]
    }
  },
  mounted () {
    // react on feature selection event
    // if feature is found this compontent will
    // be made visible
    WguEventBus.$on('map-selectionchange', (lid, newFeatureArray, oldFeatureArray) => {
      // Prevents case that de-select event from previous layer
      // fires later than the select event of the new layer
      // this fixes the bug that the new window does not open
      if (oldFeatureArray.length &&
        !newFeatureArray.length &&
        lid !== this.layerId) {
        return;
      }

      // get layer
      const layer = LayerUtil.getLayerByLid(lid, this.map);
      if (!layer) {
        return;
      }
      const poiInfo = layer.get('poiInfo');
      if (poiInfo) {
        // TODO: consider setting imageHeight per feature and not per layer
        if (poiInfo.imageHeight) {
          this.imageHeight = poiInfo.imageHeight;
        }
        if (poiInfo.windowProperties) {
          this.windowProperties = poiInfo.windowProperties;
        }
        if (poiInfo.iconProperties) {
          this.iconProperties = poiInfo.iconProperties;
        }
        if (poiInfo.customNamedProperties) {
          this.customNamedProperties = poiInfo.customNamedProperties;
        }
        if (poiInfo.excludedProperties) {
          this.excludedProperties = poiInfo.excludedProperties;
        }
        if (poiInfo.linkProperties) {
          this.linkProperties = poiInfo.linkProperties;
        }
      }

      // clear existing data
      this.windowItems = {};
      this.iconItems = {};
      this.namedItems = {};
      this.linkItems = {};
      this.properties = {};

      // check if new feature is selected
      if (newFeatureArray && newFeatureArray.length === 1) {
        const feature = newFeatureArray[0];

        // deep copy
        // static properties that will not be changed
        // used for appending to slot
        this.featureProperties = JSON.parse(JSON.stringify(feature.getProperties()));

        // deep copy
        // object will be changed during execution
        this.properties = JSON.parse(JSON.stringify(feature.getProperties()));

        // get Link

        const geometryName = feature.getGeometryName();
        this.deleteUnwantedProperties(geometryName);

        this.linkItems = this.getLinkItems();
        this.windowItems = this.getWindowItems();
        this.iconItems = this.getIconItems();
        this.namedItems = this.getNamedItems(feature);
        this.show = true;

        // necessary to for identification of fired select/deselect
        // events belong to this window or not
        this.layerId = lid;
      } else {
        this.show = false;
      }
    }
    )
  },
  methods: {
    /**
     * Delete unwanted properties.
     *
     * @param {String} geometryName The name of the feature's geometry property.
     */
    deleteUnwantedProperties (geometryName) {
      for (const key of Object.keys(this.properties)) {
        // concat arrays and properties
        let allExcludedProperties = [
          ...this.defaultExcludedProperties,
          ...this.excludedProperties,
          geometryName
        ];

        // make lower case
        allExcludedProperties = allExcludedProperties.map(x => x.toLowerCase());

        // delete unwanted properties
        if (allExcludedProperties.includes(key.toLowerCase())) {
          delete this.properties[key];
        }
      }
    },

    /**
     * Extract items that are placed as link at the bottom of the window
     * deletes them from the properties object.
     *
     * @returns {Object} The items that are shown on the window.
     */
    getLinkItems () {
      const linkItems = {};
      Object.entries(this.linkProperties).forEach(([key, config]) => {
        const value = this.properties[key];
        if (value) {
          // deep copy
          linkItems[key] = JSON.parse(
            JSON.stringify(config));

          // store item in object
          linkItems[key].url = this.properties[key];

          // delete object from property.
          // this is necessary that it will not be reused
          delete this.properties[key];
        }
      });
      return linkItems;
    },

    /**
     * Extract items that are placed on the window and not in the list
     * from the properties and deletes them from the properties object.
     *
     * @returns {Object} The items that are shown on the window.
     */
    getWindowItems () {
      const windowItems = {};

      Object.entries(this.windowProperties).forEach(([key, config]) => {
        const foundKey = this.findCorrespondingKey(config);
        if (foundKey) {
          // deep copy
          windowItems[key] = JSON.parse(
            JSON.stringify(this.windowProperties[key]));

          // store item in object
          windowItems[key].value = this.properties[foundKey];

          // delete object from property.
          // this is necessary that it will not be reused by 'getNamedItems()'
          delete this.properties[foundKey];
        }
      });

      return windowItems;
    },

    /**
     * Extract icon items from the properties and deletes them
     * from the properties object.
     *
     * @returns {Object} The items that are shown with an icon in the list.
     */
    getIconItems () {
      const iconItems = {};

      Object.entries(this.iconProperties).forEach(([key, config]) => {
        if (!config.icon) {
          console.warn(`setting for property '${key}' misses entry for 'icon'`);
          return;
        }

        const foundKey = this.findCorrespondingKey(config);
        if (foundKey) {
          // deep copy
          iconItems[key] = JSON.parse(
            JSON.stringify(this.iconProperties[key]));

          // store item in object
          iconItems[key].value = this.properties[foundKey];

          // delete object from property.
          // this is necessary that it will not be reused by 'getNamedItems()'
          delete this.properties[foundKey];
        }
      });

      return iconItems;
    },

    /**
     * Finds the key in the feature's properties that corresponds
     * to the key defined in the config.
     *
     * If many keys are given via 'possibleKeys', only the first
     * is taken.
     *
     * @param config {Object} The configuration how the the property shall be displayed.
     * @returns {String} The corresponding key of the feature.
     */
    findCorrespondingKey (config) {
      let foundKey;
      // property 'key' always has precedence over 'possibleKeys'
      if (config.key && this.properties[config.key]) {
        foundKey = config.key;
      } else if (config.possibleKeys) {
        // make lower case
        const possibleKeys = config.possibleKeys.map(x => x.toLowerCase());

        // check if in apporiate key is available in the feature
        foundKey = Object.keys(this.properties).find(prop => {
          return possibleKeys.includes(prop.toLowerCase());
        });
      } else {
        // apparently setting is wrong
        return;
      }
      return foundKey;
    },

    /**
     * Extract items from feature that should be display as simple
     * key-value pair.
     *
     * @returns {Object} The extracted item.
     */
    getNamedItems () {
      const namedItems = {};

      // we loop through properties with custom names first
      // this ensures they are added first and in the same order
      // as in the appConfig
      Object.entries(this.customNamedProperties).forEach(([key, displayName]) => {
        // find key in this.properties
        const value = this.properties[key];
        if (value) {
          namedItems[key] = {
            key: key,
            displayKey: displayName,
            value: value
          };
          delete this.properties[key];
        }
      });

      // handle remaining properties that have no special treatment
      Object.entries(this.properties).forEach(([key, value]) => {
        namedItems[key] = {
          key: key,
          value: value
        };
      });

      return namedItems;
    }
  }
}
</script>

<style scoped>
  .position{
    width: 400px;
    position: absolute;
    top: 75px;
    left: 10px;
    z-index:100;
  }

  .v-list{
    overflow-y: auto;
    max-height: 300px;
  }

  .v-list-item__title{
    padding-bottom: 1px;
  }

  .photo-attribution{
    font-size: 10px;
    margin: 0px 0px 0px 2px;
  }

  @media only screen and (max-width: 600px) {
    .position{
      width: 100%;
      top: unset;
      height: 60%;
      bottom: -2px;
      left: unset;
    }
    .card-content{
      /* necessary to show all items in mobile view */
      height: 85%;
      overflow-y: scroll;
    }
    .v-list{
      max-height: unset;
      overflow-y: unset;
    }
  }
</style>


// WEBPACK FOOTER //
// app/components/PoiInfo.vue